import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import useAuthService from 'src/shared/hooks/useAuthService';
import Dashboard from 'src/app/dashboard/Dashboard';
import Projects from 'src/app/projects/Projects';
import Tables from 'src/app/tables/Tables';
import EcologyHub from 'src/app/ecology-hub/EcologyHub';
import Admin from 'src/app/admin/Admin';
import Login from 'src/app/login/Login';
import usePermissions from 'src/shared/hooks/usePermissions';
import { Resources, Access, ProjectSampleManifestStatus } from 'src/shared/types';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSelector';
import useProject from 'src/shared/hooks/useProject';
import Survey from 'src/app/surveys/Survey';
import HabitatInsights from 'src/app/habitat-insights/HabitatInsights';
import Maps from 'src/app/maps/Maps';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Samples from 'src/app/samples/Samples';
import analyticsTracking from 'src/services/analytics-service';
import UpgradeSubscription from 'src/app/upgrade-subscription/UpgradeSubscription';
import Portfolio from 'src/app/portfolio/Portfolio';
import SiteNatureRisk from 'src/app/site-nature-risk/SiteNatureRisk';
import setRouteAnalytics from 'src/services/analytics/setRouteAnalytics';
import useNextPreview from 'src/shared/hooks/useNextPreview';
import {
    AppViews,
    IndividualRouteSetting,
    mapRoutesAvailability,
    surveyRoutesAvailability,
    insightRoutesAvailability,
} from './routesConfig';
import { getAllRoutesConfig } from './state/routesSelector';
import { updateRoutesConfig } from './state/routesSlice';
import ProtectedRoute from './ProtectedRoute';
import { getPortfolioRoutesAvailabilityForCustomer } from './appRoutesHelper';

export const appRouteSettings: { [key in AppViews]: IndividualRouteSetting } = {
    projects: {
        name: 'Projects',
        path: '/projects/*',
        handler: <Projects />,
        resource: Resources.PROJECTS,
    },
    survey: {
        name: 'Survey',
        path: '/survey/*',
        handler: <Survey />,
        resource: Resources.SURVEY_DESIGN,
    },
    habitatInsights: {
        name: 'Habitat Insights',
        path: '/habitat-insights/*',
        handler: <HabitatInsights />,
        resource: Resources.PROJECTS,
    },
    admin: {
        name: 'Admin',
        path: '/admin/*',
        handler: <Admin />,
        resource: Resources.ADMIN,
    },
    map: {
        name: 'Map',
        path: '/map/*',
        handler: <Maps />,
        resource: Resources.MAPS,
    },
    dashboard: {
        name: 'Dashboard',
        path: '/dashboard',
        handler: <Dashboard />,
        resource: Resources.DASHBOARD,
    },
    ecologyHub: {
        name: 'EcologyHub',
        path: '/ecology-hub/*',
        handler: <EcologyHub />,
        resource: Resources.ECO_STATS,
    },
    tables: {
        name: 'Tables',
        path: '/tables/*',
        handler: <Tables />,
        resource: Resources.TABLES,
    },
    login: {
        name: 'Login',
        path: '/login/*',
        handler: <Login />,
        resource: Resources.LOGIN,
    },
    samples: {
        name: 'Samples',
        path: '/samples/*',
        handler: <Samples />,
        resource: Resources.SAMPLES,
    },
    upgradeSubscription: {
        name: 'Upgrade Subscription',
        path: '/upgrade-subscription',
        handler: <UpgradeSubscription />,
        resource: Resources.UPGRADE_SUBSCRIPTION,
    },
    portfolio: {
        name: 'Portfolio',
        path: '/portfolio/*',
        handler: <Portfolio />,
        resource: Resources.PORTFOLIO,
    },
    siteNatureRisk: {
        name: 'Site Nature Risk',
        path: '/site-nature-risk/*',
        handler: <SiteNatureRisk />,
        resource: Resources.SITE_NATURE_RISK,
    },
};

const AppRoutes = () => {
    const routesUpdatedRef = useRef(false);
    const routesConfig = useAppSelector(getAllRoutesConfig);
    const dispatch = useAppDispatch();
    const { isAuthenticated, hasGivenTncConsent, isCurrentUserSuperAdmin, currentUser } = useAuthService();
    const { currentCustomerId, currentCustomerName, currentCustomerHasPortfolio } = useCustomer();

    const currentProjectId = useAppSelector(getCurrentProjectId);

    const {
        hasPermission,
        hasProjectLevelPermissionToAnyProject,
        hasCustomerLevelPermission,
        isCurrentUserInternal,
        getRolesForOneCustomer,
        getRolesForOneProject,
    } = usePermissions();
    const isCustomerAvailable = Boolean(currentCustomerId);
    const {
        isProjectAvailable,
        isCurrentProjectEmpty,
        isSurveyDesignAvailable,
        isHabitatInsightsProject,
        isEDnaProject,
        projectDefinition,
        isStarterProject,
    } = useProject();

    const location = useLocation();
    const isNextPreviewEnabled = useNextPreview();

    useEffect(() => {
        routesUpdatedRef.current = false;
    }, []);

    // Attach the Cognito ID as GA's user_id once the user is authenticated.
    // Cognito IDs are opaque UUIDs — no PII, GA4-policy-compliant as-is.
    useEffect(() => {
        const cognitoId = currentUser?.cognitoId;
        if (!cognitoId) {
            return;
        }
        analyticsTracking.setUserId(cognitoId);
    }, [currentUser?.cognitoId]);

    useEffect(() => {
        const updates: any = [];
        const { appRoutesAvailability, adminRoutesAvailability, projectRoutesAvailability, habitatInsightsRoutesAvailability } =
            routesConfig;

        // has species map access
        const isSpeciesMapAvailable =
            isProjectAvailable &&
            (isEDnaProject || !isCurrentProjectEmpty) &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.SPECIES_INSIGHTS_MAP,
            });

        const hasHabitatInsightsPageAccess =
            isProjectAvailable &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.HABITAT_INSIGHTS_PAGE,
            });

        const hasDashboardAccess =
            isProjectAvailable &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.DASHBOARD,
            });

        const hasEcoStatPageAccess = hasPermission({
            to: Access.VIEW,
            resource: Resources.ECO_STATS,
        });

        const hasTablesAccess = hasPermission({
            to: Access.VIEW,
            resource: Resources.TABLES,
        });

        updates.push(
            updateRoutesConfig({
                appRoutesAvailability: {
                    ...appRoutesAvailability,
                    projects: isCustomerAvailable,
                    survey: isCustomerAvailable,
                    habitatInsights: isCustomerAvailable,
                    admin: isCustomerAvailable,
                    dashboard: hasDashboardAccess,
                    ecologyHub: (hasEcoStatPageAccess && isProjectAvailable) || (isHabitatInsightsProject && hasHabitatInsightsPageAccess),
                    tables: (hasTablesAccess && !isCurrentProjectEmpty) || (isHabitatInsightsProject && hasHabitatInsightsPageAccess),
                    map: true,
                    samples: isAuthenticated,
                    portfolio: isCustomerAvailable,
                    siteNatureRisk: isCustomerAvailable,
                },
            })
        );

        // Admin routes
        const isUserManagementAvailable = [
            isCustomerAvailable,
            [
                hasPermission({
                    to: Access.VIEW,
                    resource: Resources.USER,
                }),
                hasCustomerLevelPermission({
                    to: Access.VIEW,
                    resource: Resources.USER,
                    customerId: currentCustomerId,
                }),
                hasProjectLevelPermissionToAnyProject({
                    to: Access.VIEW,
                    resource: Resources.USER,
                    customerId: currentCustomerId,
                }),
            ].some(Boolean),
        ].every(Boolean);

        const isRiskPortfolioAdminAvailable = isCustomerAvailable && isCurrentUserSuperAdmin;
        const isEngineApiTokensAvailable = isCustomerAvailable && isCurrentUserSuperAdmin;
        const isNaturePerformanceAvailable = isCurrentUserSuperAdmin;

        updates.push(
            updateRoutesConfig({
                adminRoutesAvailability: {
                    ...adminRoutesAvailability,
                    gnpManagement: isNaturePerformanceAvailable,
                    organisations: isNaturePerformanceAvailable,
                    userManagement: isUserManagementAvailable,
                    riskPortfolio: isRiskPortfolioAdminAvailable,
                    engineApiTokens: isEngineApiTokensAvailable,
                },
            })
        );

        // Projects routes config
        const isProjectDefinitionAvailable =
            isCustomerAvailable &&
            isProjectAvailable &&
            isCurrentUserInternal &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.PROJECT_DEFINITION,
            });

        const isNewProjectDefinitionAvailable =
            isCustomerAvailable &&
            hasPermission({
                to: Access.CREATE,
                resource: Resources.PROJECT_DEFINITION,
            });

        const isEditProjectDefinitionAvailable =
            isCustomerAvailable &&
            hasPermission({
                to: Access.UPDATE,
                resource: Resources.PROJECT_DEFINITION,
            });

        updates.push(
            updateRoutesConfig({
                projectRoutesAvailability: {
                    ...projectRoutesAvailability,
                    projectDefinition: isProjectDefinitionAvailable,
                    newProjectDefinition: isNewProjectDefinitionAvailable,
                    editProjectDefinition: isEditProjectDefinitionAvailable,
                    freemiumSpeciesInsights: isHabitatInsightsProject && !isEDnaProject,
                    siteFiles: isProjectAvailable,
                    siteSummary: isProjectAvailable,
                },
            })
        );

        // Habitat insights routes config
        updates.push(
            updateRoutesConfig({
                habitatInsightsRoutesAvailability: {
                    ...habitatInsightsRoutesAvailability,
                    habitatInsightsUpload: hasPermission({
                        to: Access.CREATE,
                        resource: Resources.HABITAT_INSIGHTS_PAGE,
                    }),
                    habitatInsightsAddSite: true,
                },
            })
        );

        // Map routes config
        updates.push(
            updateRoutesConfig({
                mapRoutesAvailability: {
                    ...mapRoutesAvailability,
                    speciesMap: isSpeciesMapAvailable,
                    habitatMap: isProjectAvailable,
                },
            })
        );

        // Survey routes config
        const canAdminSurveyDesignAvailable =
            isCustomerAvailable &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.SURVEY_DESIGN,
            });

        const isNewSurveyDesignAvailable =
            isCustomerAvailable &&
            hasPermission({
                to: Access.CREATE,
                resource: Resources.SURVEY_DESIGN,
            });

        const isSurveyDesignSummaryAvailable =
            isProjectAvailable &&
            isSurveyDesignAvailable &&
            isCustomerAvailable &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.SURVEY_DESIGN,
            });

        updates.push(
            updateRoutesConfig({
                surveyRoutesAvailability: {
                    ...surveyRoutesAvailability,
                    surveyAdmin: canAdminSurveyDesignAvailable,
                    newSurveyDesign: isNewSurveyDesignAvailable,
                    surveyDesignSummary: isSurveyDesignSummaryAvailable || false,
                },
            })
        );

        const sampleManifestStatus = projectDefinition?.sampleManifestStatus || ProjectSampleManifestStatus.NOT_APPLICABLE;
        // Samples route config
        const isSamplesManifestAvailable =
            sampleManifestStatus !== ProjectSampleManifestStatus.NOT_APPLICABLE &&
            hasPermission({
                to: Access.VIEW,
                resource: Resources.SAMPLE_MANIFEST,
            });
        const isSampleReceptionAvailable = hasPermission({
            to: Access.VIEW,
            resource: Resources.SAMPLE_RECEPTION,
        });

        updates.push(
            updateRoutesConfig({
                samplesRoutesAvailability: {
                    samples: true,
                    sampleManifest: isSamplesManifestAvailable,
                    sampleReception: isSampleReceptionAvailable,
                    sampleBatchPrint: isSamplesManifestAvailable,
                },
            })
        );

        //ecologyHub routes config
        updates.push(
            updateRoutesConfig({
                insightRoutesAvailability: {
                    ...insightRoutesAvailability,
                    habitatTrends: isHabitatInsightsProject || isStarterProject,
                },
            })
        );

        // Portfolio routes config
        updates.push(
            updateRoutesConfig({
                portfolioRoutesAvailability: getPortfolioRoutesAvailabilityForCustomer({
                    isCustomerAvailable,
                    currentCustomerHasPortfolio,
                    isCurrentUserInternal,
                    isNextPreviewEnabled,
                }),
            })
        );

        updates.map(dispatch);

        routesUpdatedRef.current = true;
    }, [
        isAuthenticated,
        currentProjectId,
        currentCustomerId,
        isCustomerAvailable,
        isProjectAvailable,
        isCurrentProjectEmpty,
        currentCustomerHasPortfolio,
        isHabitatInsightsProject,
        isStarterProject,
        isEDnaProject,
        // Resolves from /my_permissions after the first run, and now gates route
        // availability rather than only the nav — a stale false locks internal
        // users out of the pages the sidebar is offering them.
        isCurrentUserInternal,
        // Changes the moment ?next= is used, and the preview pages must appear or
        // disappear on that navigation rather than on the one after it.
        isNextPreviewEnabled,
    ]);

    if (!routesUpdatedRef.current) {
        return null;
    }

    const shouldRedirectToProjects = isCustomerAvailable && !isProjectAvailable && routesConfig.appRoutesAvailability.projects;

    // Update GA config params
    const configUpdateParams = {
        location,
        isAuthenticated,
        projectDefinition,
        isCurrentUserInternal,
        currentCustomerName,
        currentCustomerId,
        currentProjectId: currentProjectId || undefined,
        currentCustomerRoles: currentCustomerId ? getRolesForOneCustomer(currentCustomerId) : undefined,
        currentProjectRoles: currentProjectId ? getRolesForOneProject(currentProjectId) : undefined,
        currentUserEmail: currentUser?.email,
    };
    setRouteAnalytics(configUpdateParams);

    return (
        <Routes>
            {Object.keys(routesConfig.appRoutesAvailability).map(routeKey => {
                const redirectTo = window.location.pathname + window.location.search;
                let redirectPath = '/?redirectTo=' + encodeURIComponent(redirectTo);
                if (isAuthenticated && !hasGivenTncConsent) {
                    redirectPath = '/login/terms-of-use-declined';
                } else if (
                    shouldRedirectToProjects &&
                    routeKey !== AppViews.ADMIN &&
                    routeKey !== AppViews.PORTFOLIO &&
                    routeKey !== AppViews.SITE_NATURE_RISK
                ) {
                    // Admin, portfolio, and site nature risk are valid without a selected site; do not send their gate redirects to My Sites.
                    redirectPath = appRouteSettings.projects.path;
                }

                const isAvailable = (isAuthenticated || routeKey === 'login') && routesConfig.appRoutesAvailability[routeKey as AppViews];
                const routeSetting = appRouteSettings[routeKey as AppViews];
                return (
                    <Route
                        path={routeSetting.path}
                        element={
                            <ProtectedRoute isAllowed={isAvailable} redirectPath={redirectPath} routeSetting={routeSetting}>
                                {routeSetting.handler}
                            </ProtectedRoute>
                        }
                        key={routeSetting.name}
                    />
                );
            })}
            <Route path='/' element={<Login />} />
            <Route path='/login/*' element={<Login />} />
            <Route path='*' element={<PageNotFound />} />
        </Routes>
    );
};

export default AppRoutes;
